import { ChartOptions } from "chart.js";
import { useState } from "react";
import { Bar } from "react-chartjs-2";
import Button from "@mui/material/Button";
import { Grid, Typography } from "@mui/material";
import styles from "./styles";
import { useAppSelector } from "hooks/reduxHooks";
import {
  selectCurrentPatientDailyCheckinData,
  selectCurrentPatientDailyCheckinWeekData,
  selectCurrentPatientDailyCheckinYearData,
  selectLoading,
} from "store/PatientStore/selectors";
import { DailyCheckIn } from "types/Patient";
import Skeleton from "react-loading-skeleton";
import moment from "moment";

type Props = {
  selectedMonth: number;
  isFromCronJob?: boolean;
};
const PainProgressChart = ({ selectedMonth, isFromCronJob }: Props) => {
  const [selectedOption, setSelectedOption] = useState("month");
  const patientDailyCheckin = useAppSelector(
    selectCurrentPatientDailyCheckinData
  );
  const patientDailyCheckinWeek = useAppSelector(
    selectCurrentPatientDailyCheckinWeekData
  );
  const patientDailyCheckinYear = useAppSelector(
    selectCurrentPatientDailyCheckinYearData
  );
  const patientLoading = useAppSelector(selectLoading);

  const groupPainLevelData = (data: DailyCheckIn[]) => {
    if (!data) {
      return [];
    }
    return data.reduce((acc: any, obj: any) => {
      const date = new Date(obj.createdAt);
      const day = date.getDate();
      const month = date.getMonth() + 1; // Get the current month (1-indexed)
      const year = date.getFullYear(); // Get the current year
      const weekDay = moment(date).format("ddd");
      if (!acc[day]) {
        acc[day] = {
          day: day,
          month: month,
          year: year,
          weekDay: weekDay,
          levels: [],
        };
      }
      if (obj.painLevel !== "Skip") {
        acc[day].levels.push(obj.painLevel);
      }
      return acc;
    }, {});
  };

  const handleOptionChange = (option: any) => {
    setSelectedOption(option);
  };

  const updatedLabels = () => {
    let labels: any = [];
    if (selectedOption === "month") {
      const year = new Date().getFullYear();
      const month = selectedMonth + 1;
      const lastDayOfMonth = new Date(year, month, 0).getDate();
      labels = Array.from({ length: lastDayOfMonth }, (_, index) =>
        (index + 1).toString()
      );
    } else if (selectedOption === "year") {
      const monthsInYear = 12;
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      labels = Array.from(
        { length: monthsInYear },
        (_, index) => monthNames[index]
      );
    } else if (selectedOption === "week") {
      labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    }
    return labels;
  };

  const updatedData = () => {
    if (selectedOption === "month") {
      const groupedData = groupPainLevelData(
        patientDailyCheckin?.data?.painLevelData ?? []
      );
      return updatedLabels().map(
        (day: string) =>
          groupedData[parseInt(day)]?.levels.reduce(
            (sum: any, level: any) => sum + level,
            0
          ) || 0
      );
    } else if (selectedOption === "year") {
      const groupedData = groupPainLevelData(
        patientDailyCheckinYear?.data?.painLevelData ?? []
      );
      return updatedLabels().map((month: string, index: number) => {
        const monthIndex = index + 1;
        const monthData = Object.values(groupedData).filter(
          (data: any) => data.month === monthIndex
        );
        return monthData.reduce(
          (sum: any, data: any) =>
            sum +
            data.levels.reduce((subSum: any, level: any) => subSum + level, 0),
          0
        );
      });
    } else if (selectedOption === "week") {
      const groupedData = groupPainLevelData(
        patientDailyCheckinWeek?.data?.painLevelData ?? []
      );
      // Create an array to store the sum of levels for each weekday
      const sumByWeekday = Array(7).fill(0);
      Object.values(groupedData).forEach((data: any) => {
        const { weekDay, levels } = data;
        // Add the sum of levels to the corresponding weekday in the sumByWeekday array
        const weekdayIndex = moment.weekdaysShort().indexOf(weekDay);
        if (weekdayIndex !== -1) {
          sumByWeekday[weekdayIndex] += levels.reduce(
            (acc: any, curr: any) => acc + curr,
            0
          );
        }
      });
      return sumByWeekday;
    }
  };

  const data = {
    labels: updatedLabels(),
    datasets: [
      {
        label: "Pain Level",
        backgroundColor: "#BD9FF3",
        borderRadius: 10,
        data: updatedData() ?? [],
        borderWidth: 0,
        barThickness: 6,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          color: (context) => {
            const yValues = updatedData();
            const yValue = yValues?.[context.index];
            if (!isNaN(yValue) && yValue > 0) {
              return "#5C5D68";
            }
            return "#DDDDDD";
          },
          font: (context) => {
            return { weight: "bold" };
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        grid: {
          display: true,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <Grid container>
      {patientLoading ? (
        <Skeleton height={195} width={500} />
      ) : (
        <>
          <Typography sx={styles.chartTitle}>
            Pain Level Progress Chart
          </Typography>
          <Grid sx={styles.chartContainer}>
            <Bar data={data} options={options} height={200} />
          </Grid>
          {!isFromCronJob && (
            <div style={styles.buttonContainer}>
              <Button
                variant={"contained"}
                style={
                  selectedOption === "month"
                    ? styles.selectedButtonBottom
                    : styles.buttonBottom
                }
                onClick={() => handleOptionChange("month")}
              >
                Month
              </Button>
              <Button
                variant={"contained"}
                style={
                  selectedOption === "week"
                    ? styles.selectedButtonBottom
                    : styles.buttonBottom
                }
                onClick={() => handleOptionChange("week")}
              >
                Week
              </Button>
              <Button
                variant={"contained"}
                style={
                  selectedOption === "year"
                    ? styles.selectedButtonBottom
                    : styles.buttonBottom
                }
                onClick={() => handleOptionChange("year")}
              >
                Year
              </Button>
            </div>
          )}
        </>
      )}
    </Grid>
  );
};

export default PainProgressChart;
